import React, { useEffect, useMemo, useRef } from "react";

/** Deep-ish hash so memo comparator can skip re-renders when content is the same */
function hashTestimonials(arr = []) {
  let h = 2166136261 >>> 0; // FNV-1a
  for (let i = 0; i < arr.length; i++) {
    const t = `${arr[i]?.testimonial ?? ""}|${arr[i]?.role ?? ""}`;
    for (let j = 0; j < t.length; j++) {
      h ^= t.charCodeAt(j);
      h = (h * 16777619) >>> 0;
    }
  }
  return h;
}

/** Deterministic shuffle (seedless but stable for same input ordering) */
function stableShuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = (i * 9301 + 49297) % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Repeat items until we have enough cards for a smooth run */
function buildSequence(base, minCards = 12) {
  if (!base.length) return [];
  const out = [];
  while (out.length < minCards) out.push(...base);
  return out.slice(0, Math.max(minCards, base.length));
}

function Card({ t }) {
  return (
    <div
      style={{
        background: "rgba(20, 25, 40, 0.65)",
        color: "#E8EEF7",
        borderRadius: 12,
        padding: "18px 20px",
        minWidth: 440,
        maxWidth: 620,
        boxShadow: "0 10px 28px rgba(0,0,0,0.20), inset 0 0 0 1px rgba(255,255,255,0.06)",
        backdropFilter: "blur(2px)",
        fontSize: "1rem",
        lineHeight: 1.6,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        marginLeft: "2rem",
        marginRight: "2rem",
      }}
    >
      <div>“{t.testimonial}”</div>
      {t.role ? (
        <div style={{ marginTop: 8, opacity: 0.85, fontWeight: 600 }}>— {t.role}</div>
      ) : null}
    </div>
  );
}

const Row = React.memo(function Row({ items, pxPerSec = 32, reverse = false, rowKey = "row" }) {
  const wrapperRef = React.useRef(null);
  const seqARef = React.useRef(null);
  const widthRef = React.useRef(0);
  const offsetRef = React.useRef(0);
  const lastTsRef = React.useRef(0);
  const rafRef = React.useRef(0);
  const pausedRef = React.useRef(false);

  // Measure AFTER fonts render; initialize offset correctly for direction.
  React.useEffect(() => {
    let cancelled = false;

    const measure = () => {
      if (!seqARef.current) return;
      const w = Math.ceil(seqARef.current.getBoundingClientRect().width);
      if (w > 0) {
        if (widthRef.current === 0) {
          // first measure: initialize offset so reverse shows cards entering from the left
          widthRef.current = w;
          offsetRef.current = reverse ? -w : 0;   // ✅ important
        } else {
          // preserve progress proportionally if width changes
          const prevW = widthRef.current;
          const ratio = w / prevW;
          offsetRef.current *= ratio;
          widthRef.current = w;
          // normalize to our ranges after resize
          if (!reverse) {
            // keep in [-w, 0)
            if (offsetRef.current <= -w) offsetRef.current += w;
            if (offsetRef.current > 0) offsetRef.current -= w;
          } else {
            // keep in [-w, 0) for reverse as well (we animate toward 0)
            if (offsetRef.current < -w) offsetRef.current += w;
            if (offsetRef.current >= 0) offsetRef.current -= w;
          }
        }
      }
    };

    const init = async () => {
      try {
        if (document?.fonts?.ready) await document.fonts.ready;
      } catch {}
      if (!cancelled) requestAnimationFrame(measure);
    };

    init();

    const ro = seqARef.current ? new ResizeObserver(() => requestAnimationFrame(measure)) : null;
    if (seqARef.current) ro.observe(seqARef.current);

    const onResize = () => requestAnimationFrame(measure);
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      ro && ro.disconnect();
    };
  }, [reverse]);

  // rAF loop (keeps offset in [-w, 0) for BOTH directions)
  React.useEffect(() => {
    const step = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      const w = widthRef.current;
      const wrap = wrapperRef.current;

      if (wrap && w > 0 && !pausedRef.current) {
        const dir = reverse ? 1 : -1; // left when false, right when true
        offsetRef.current += dir * pxPerSec * dt;

        // Normalize into [-w, 0)
        if (offsetRef.current <= -w) offsetRef.current += w;
        if (offsetRef.current > 0) offsetRef.current -= w;

        wrap.style.transform = `translate3d(${Math.round(offsetRef.current)}px,0,0)`;
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    const onVis = () => { lastTsRef.current = 0; };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [pxPerSec, reverse]);

  return (
    <div style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
      <div
        ref={wrapperRef}
        style={{ display: "inline-flex", willChange: "transform", gap: 0 }}
        onMouseEnter={() => (pausedRef.current = true)}
        onMouseLeave={() => (pausedRef.current = false)}
      >
        {/* Sequence A (measured) */}
        <div ref={seqARef} style={{ display: "flex", alignItems: "stretch", gap: "16px", padding: "6px 0" }}>
          {items.map((t, i) => (
            <Card key={`${rowKey}-A-${i}`} t={t} />
          ))}
        </div>
        {/* Sequence B (exact clone) */}
        <div style={{ display: "flex", alignItems: "stretch", gap: "16px", padding: "6px 0" }}>
          {items.map((t, i) => (
            <Card key={`${rowKey}-B-${i}`} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
});


function TestimonialsSectionInner({ testimonials = [] }) {
  // Build STABLE sequences exactly once per content change.
  const contentHash = useMemo(() => hashTestimonials(testimonials), [testimonials]);

  const rowARef = useRef([]);
  const rowBRef = useRef([]);
  const hashRef = useRef(null);

  if (hashRef.current !== contentHash) {
    // Freeze new orders only when content actually changes
    const ordered = stableShuffle(testimonials);
    rowARef.current = buildSequence(ordered, 12);
    rowBRef.current = buildSequence([...ordered].reverse(), 12);
    hashRef.current = contentHash;
  }

  const sectionBg =
    "radial-gradient(900px 400px at 50% 20%, rgba(255,255,255,0.06), rgba(255,255,255,0) 60%), linear-gradient(180deg, #262F3E 0%, #2F3C51 40%, #35455E 100%)";

  return (
    <div
      style={{
        position: "relative",
        padding: "80px 6% 64px",
        background: sectionBg,
        color: "#fff",
        overflow: "hidden",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <h2
          style={{
            margin: 0,
            fontSize: "clamp(2rem, 6vw, 3.5rem)",
            fontWeight: 800,
            letterSpacing: "-0.01em",
          }}
        >
          Trusted by creators
        </h2>
        <p
          style={{
            margin: "10px 0 0",
            color: "rgba(232,238,247,0.85)",
            fontSize: "1.05rem",
          }}
        >
          The secret weapon behind smarter video.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: "auto auto",
          gap: "18px",
          marginTop: 24,
          maskImage:
            "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
        }}
      >
        <Row items={rowARef.current} pxPerSec={32} reverse={false} rowKey="rowA" />
        <Row items={rowBRef.current} pxPerSec={36} reverse={true} rowKey="rowB" />
      </div>
    </div>
  );
}

/** Export memo-wrapped component that only re-renders if the testimonial CONTENT changes */
export default React.memo(
  TestimonialsSectionInner,
  (prevProps, nextProps) => hashTestimonials(prevProps.testimonials) === hashTestimonials(nextProps.testimonials)
);
